<script>
import AkLabel from '@components/v2/general/AkLabel';
import MultiSelect from 'primevue/multiselect';
import UtilsMixin from '@mixins/utilsMixin';

export default {
  components: {AkLabel, MultiSelect},
  mixins: [UtilsMixin],
  inject: {pSubmitted: {default: null}, pDisabled: {default: null}, pIgnoredValidator: {default: null}},
  props: {
    label: {
      type: String,
      required: true,
    },
    modelValue: {
      required: true,
    },
    placeholder: {
      type: String,
      required: false,
      default: undefined,
    },
    className: {
      type: String,
      required: false,
      default: '',
    },
    options: {
      type: Array,
      required: true,
    },
    optionLabel: {
      type: [String, Function],
      required: false,
      default: 'label',
    },
    optionValue: {
      type: [String, Function],
      required: false,
      default: 'value',
    },
    filter: {
      type: Boolean,
      required: false,
      default: false,
    },
    showToggleAll: {
      type: Boolean,
      required: false,
      default: true,
    },
    validator: {
      type: Object,
      required: false,
      default: undefined,
    },
    ignoredValidator: {
      type: Array,
      required: false,
      default: undefined,
    },
    submitted: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    disabled: {
      type: Boolean,
      required: false,
      default: undefined,
    },
    editable: {
      type: Boolean,
      required: false,
      default: false,
    },
    loading: {
      type: Boolean,
      required: false,
      default: false,
    },
    optionDisabled: {
      type: [Boolean, String],
      required: false,
      default: false,
    },
    link: {
      type: Object,
      required: false,
      default: undefined,
    },
    separator: {
      type: String,
      required: false,
      default: ',',
    },
    returnType: {
      type: String,
      required: false,
      default: 'array',
    },
    blurEvent: {
      type: Function,
      required: false,
      default: undefined,
    },
    displayLabel: {
      type: Boolean,
      required: false,
      default: true,
    },
    optionGroupChildren: {
      type: String,
      required: false,
      default: undefined,
    },
    optionGroupLabel: {
      type: String,
      required: false,
      default: undefined,
    },
    onlyOneInGroup: {
      type: Boolean,
      required: false,
      default: false,
    },
    information: {
      type: String,
      required: false,
      default: undefined,
    },
  },
  emits: ['update:modelValue'],
  computed: {
    currentValue: {
      get: function () {
        if (this.returnType === 'array') return this.modelValue;
        else {
          if (!this.modelValue || this.modelValue === '') return [];
          return this.modelValue.split(this.separator);
        }
      },
      set: function (val) {
        if (this.returnType === 'array') this.$emit('update:modelValue', val);
        else {
          this.$emit('update:modelValue', val.filter((el) => el !== '').join(this.separator));
        }
      },
    },
    getPlaceholder() {
      return this.placeholder ? this.placeholder : this.label;
    },
    getIgnoredValidator() {
      if (this.ignoredValidator != undefined) return this.ignoredValidator;
      if (this.pIgnoredValidator != undefined) return this.pIgnoredValidator;
      return [];
    },
    alreadySubmitted() {
      if (this.submitted !== undefined) return this.submitted;
      if (this.pSubmitted !== undefined) return this.pSubmitted;
      return false;
    },
    isDisabled() {
      if (this.disabled !== undefined) return this.disabled;
      if (this.pDisabled !== undefined) return this.pDisabled;
      return false;
    },
    hasValidator() {
      return this.validator !== undefined;
    },
    isInvalid() {
      if (this.hasValidator && this.getIgnoredValidator.length > 0) {
        for (let error of this.validator.$errors) {
          if (this.getIgnoredValidator.indexOf(error.$validator) === -1) {
            return true;
          }
        }
      } else {
        return this.hasValidator && this.validator.$invalid;
      }
      return false;
    },
    isRequired() {
      return this.validator !== undefined && this.validator.required !== undefined;
    },
    isValidatorRequired() {
      return this.isRequired && this.isInvalid;
    },
    optionList() {
      if (!this.options) return [];
      if (this.isRequired) {
        return this.options.filter(this.toFunction(this.optionValue));
      }
      return this.options;
    },
  },
  methods: {
    handleChange(event) {
      if (this.onlyOneInGroup) {
        const newSelected = [];
        const newValues = event.value;

        // Pour chaque groupe, on garde au plus un élément
        for (const group of this.optionList) {
          const groupValues = group.items.map((item) => item.value);
          // Trouve la dernière valeur sélectionnée qui appartient à ce groupe
          const selectedInGroup = [...newValues].reverse().find((val) => groupValues.includes(val));
          if (selectedInGroup) {
            newSelected.push(selectedInGroup);
          }
        }
        this.currentValue = newSelected;
      } else {
        this.currentValue = event.value;
      }
    },
    blurEventInternally() {
      if (this.blurEvent) {
        this.blurEvent();
      }
    },
  },
};
</script>

<template>
  <div class="w-full input-layout form-control" :class="[className]">
    <AkLabel v-if="this.displayLabel" :required="isRequired" :information="this.information">
      {{ label }}
    </AkLabel>
    <MultiSelect
      @change="handleChange"
      v-model="currentValue"
      :class="{'p-invalid': isInvalid && alreadySubmitted}"
      :disabled="isDisabled"
      :display-label="displayLabel"
      :editable="editable"
      :filter="filter"
      :show-toggle-all="showToggleAll"
      :loading="loading"
      :option-disabled="optionDisabled"
      :option-group-children="optionGroupChildren"
      :option-group-label="optionGroupLabel"
      :option-label="optionLabel"
      :option-value="optionValue"
      :options="optionList"
      :filter-placeholder="$t('filter')"
      :placeholder="getPlaceholder"
      class="w-full text-grey-900 font-inter form-control"
      @blur="blurEventInternally" />
    <router-link v-if="isDisabled && link !== undefined" :to="{name: link.route, params: link.params}">
      <i :class="link.icon" class="icon-link" />
    </router-link>
    <div class="flex flex-col flex-1">
      <small v-if="isValidatorRequired && alreadySubmitted" class="p-error">{{ $t('field_required', [label]) }}</small>
    </div>
  </div>
</template>
